import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { myId } from "../lib/me";
import {
  equipmentCount, exerciseCount, libraryTabs, programCount, workoutCount,
  type LibraryCounts, type LibraryPart,
} from "../lib/libraryTabs";

/* W72 STEP 5 (2) — THE LIBRARY'S FOUR PARTS as a segmented UNDERLINE row under the title, each with its count. It
 * replaces the Library's left sub-nav (`ListNav` in the workspace's second column) AND its phone copy
 * (`PhoneSegment`): one row at every width, so there is one control and not two drawn for two widths.
 *
 * Links, not radios: each part is its own address (`#/workouts`, `#/exercises` …), and choosing one navigates.
 * `aria-current="page"` says which is open, the accent underline shows it.
 *
 * The counts are four narrow reads, each OWNER-NAMED (`lib/me.ts`: a coach who is somebody's client can read two
 * libraries), each counted by the rule its own page lists by (`lib/libraryTabs.ts`). A read that fails leaves its
 * count out — the word stands alone rather than claiming zero.
 */
export function useLibraryCounts(reloadKey = 0): LibraryCounts {
  const [counts, setCounts] = useState<LibraryCounts>({ workouts: null, exercises: null, programs: null, equipment: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await myId();
      const [w, e, p, q] = await Promise.all([
        supabase.from("coach_workouts").select("client_id, archived_at").eq("coach_id", me).is("client_id", null),
        supabase.from("coach_exercises").select("hidden_at").eq("coach_id", me),
        supabase.from("coach_programs").select("archived_at").eq("coach_id", me),
        supabase.from("coach_equipment").select("key").eq("coach_id", me),
      ]);
      if (cancelled) return;
      setCounts({
        workouts: w.error === null ? workoutCount(w.data ?? []) : null,
        exercises: e.error === null ? exerciseCount(e.data ?? []) : null,
        programs: p.error === null ? programCount(p.data ?? []) : null,
        equipment: q.error === null ? equipmentCount(q.data ?? []) : null,
      });
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);
  return counts;
}

export function LibraryTabs({ current, counts }: { current: LibraryPart; counts: LibraryCounts }) {
  return (
    <nav aria-label="Library">
      <ul className="library-tabs">
        {libraryTabs(current, counts).map((t) => (
          <li key={t.part}>
            <a className="library-tab" href={t.href} aria-current={t.current ? "page" : undefined}>
              {t.label}
              {t.count !== null && <span className="library-tab-count">{t.count}</span>}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
